import React from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "outline" | "destructive";
  size?: "default" | "sm" | "lg" | "icon";
  isLoading?: boolean;
}

const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary:
    "bg-primary text-primary-foreground shadow-sm hover:bg-primary-dark active:bg-primary-dark/90 focus-visible:ring-primary/40",
  secondary:
    "bg-secondary text-secondary-foreground shadow-sm hover:bg-amber-600 active:bg-amber-700 focus-visible:ring-secondary/40",
  ghost:
    "text-foreground-secondary hover:bg-muted hover:text-foreground focus-visible:ring-primary/40",
  outline:
    "border border-border bg-transparent text-foreground hover:bg-muted hover:border-border-hover focus-visible:ring-primary/40",
  destructive:
    "bg-error text-error-foreground shadow-sm hover:bg-red-600 active:bg-red-700 focus-visible:ring-error/40",
};

const sizeClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
  default: "h-10 px-5 py-2 text-sm",
  sm: "h-9 px-3.5 py-1.5 text-xs rounded-[var(--radius-sm)]",
  lg: "h-12 px-8 py-3 text-base",
  icon: "h-10 w-10",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "default",
      isLoading,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded font-medium",
          "transition-all duration-base",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "disabled:pointer-events-none disabled:opacity-50",
          "active:scale-[0.98]",
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        disabled={isLoading || disabled}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading && (
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";
